import clsx from 'clsx'
import styles from './PhotoTile.module.css'

export type PhotoTileStatus = 'queued' | 'uploading' | 'done' | 'error'

interface PhotoTileProps {
  /** A local preview (object URL) or a stored image URL; none → an empty tile. */
  src: string | null
  width?: number
  height?: number
  status: PhotoTileStatus
  /** 0–1 while uploading. */
  progress: number
  /** The line on a failed upload (falls back to "Upload failed"). */
  error?: string | null
  /** Shown on a failed tile when given. */
  onRetry?: () => void
  onRemove: () => void
  /** The ✕ button's label. */
  removeLabel?: string
  /** The queued tile's line: "Waiting…" by default; `null` shows none (the owner band holds picks
   *  until its sign-in at Publish, so a held photo is just its thumbnail). */
  queuedLabel?: string | null
  /** Host overlays (the writer's Cover and pending line). */
  children?: React.ReactNode
}

function statusLine(
  status: PhotoTileStatus,
  progress: number,
  error?: string | null,
  queuedLabel: string | null = 'Waiting…'
): string | null {
  switch (status) {
    case 'queued':
      return queuedLabel
    case 'uploading':
      return `${Math.round(progress * 100)}%`
    case 'error':
      return error ?? 'Upload failed'
    default:
      return null
  }
}

/**
 * One uploaded-photo tile: the image, a progress bar while uploading, the status ("Waiting…",
 * "42%", the error with an optional "Try again") and ✕. Used by the discussions writer's photo
 * sets (`ImagesBlock`) and Help & support's screenshots; the upload itself is the host's.
 */
const PhotoTile: React.VFC<PhotoTileProps> = ({
  src,
  width,
  height,
  status,
  progress,
  error,
  onRetry,
  onRemove,
  removeLabel = 'Remove photo',
  queuedLabel,
  children,
}) => {
  const line = statusLine(status, progress, error, queuedLabel)
  return (
    <div className={styles.tile}>
      {src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          className={styles.tileImg}
          src={src}
          alt=""
          width={width || undefined}
          height={height || undefined}
          loading="lazy"
          decoding="async"
        />
      )}
      {status === 'uploading' && (
        <span className={styles.progress} aria-hidden="true">
          <span
            className={styles.progressBar}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </span>
      )}
      {line && (
        <span
          className={clsx(styles.tileStatus, { [styles.tileError]: status === 'error' })}
          role={status === 'error' ? 'alert' : undefined}
        >
          {line}
          {status === 'error' && onRetry && (
            <button type="button" className={styles.retry} onClick={onRetry}>
              {'Try again'}
            </button>
          )}
        </span>
      )}
      {children}
      <button
        type="button"
        className={styles.tileRemove}
        aria-label={removeLabel}
        onClick={onRemove}
      >
        {'✕'}
      </button>
    </div>
  )
}

export default PhotoTile
